import { useState, useEffect } from 'react'
import { Modal, Button, Space, Typography, Switch, Tour } from 'antd'
import { Route, RoutingType } from '../routingApi'

const { Text } = Typography

type Props = {
  open: boolean
  onCancel: () => void
  onSave: (saveStrategy: 'regenerate' | 'update' | 'update-then-activate' | 'update-then-deactivate') => void
  route: Route
  initialActiveState?: boolean
  originalActiveState?: boolean
}

const SaveRouteModal = ({ 
  open, 
  onCancel, 
  onSave, 
  route,
  initialActiveState = false,
  originalActiveState = false
}: Props) => {
  const [activateRoute, setActivateRoute] = useState(initialActiveState)
  const viewed = window.localStorage.getItem('save-route-modal-tour-viewed') === 'true'
  const [isTourOpen, setIsTourOpen] = useState(!viewed && open)

  useEffect(() => {
    setActivateRoute(initialActiveState)
  }, [initialActiveState])
  
  useEffect(() => {
    if (open && !viewed) {
      const timer = setTimeout(() => {
        setIsTourOpen(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [open, viewed])

  const handleSave = () => {
    let saveStrategy: 'regenerate' | 'update' | 'update-then-activate' | 'update-then-deactivate'
    
    if (originalActiveState && activateRoute && route.routeType === RoutingType.Planned) {
      saveStrategy = 'regenerate'
    } else if (originalActiveState && activateRoute && route.routeType === RoutingType.OnDemand) {
      saveStrategy = 'update-then-activate'
    }
    else if (!originalActiveState && !activateRoute) {
      saveStrategy = 'update'
    } else if (!originalActiveState && activateRoute) {
      saveStrategy = 'update-then-activate'
    } else if (originalActiveState && !activateRoute) {
      saveStrategy = 'update-then-deactivate'
    } else {
      saveStrategy = 'update'
    }
    
    onSave(saveStrategy)
    onCancel()
  }

  return <>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>Save Route</span>
          </div>
        }
        open={open}
        onCancel={onCancel}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            id="save-route-action-button"
            key="save"
            type="primary"
            onClick={handleSave}
          >
            {activateRoute && route.routeType === RoutingType.Planned ? 'Update Calendar Events' : 'Save Route'}
          </Button>
        ]}
        width={500}
      >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div id="activate-route-switch" style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Switch
            checked={activateRoute}
            onChange={setActivateRoute}
          />
          <span>Activate Route</span>
        </div>
        {route.routeType === RoutingType.Planned &&
          <Text>
            {activateRoute 
              ? `Saving ${route.name} will update future calendar events.`
              : `Saving ${route.name} will remove any existing future scheduled events from the calendar.`
            }
          </Text>
        }
      </Space>
      </Modal>
      
      <Tour
        closable={false}
        open={isTourOpen && open}
        onClose={() => setIsTourOpen(false)}
        onFinish={() => {
          setIsTourOpen(false)
          window.localStorage.setItem('save-route-modal-tour-viewed', 'true')
        }}
        steps={[
          {
            title: 'Activate/Inactivate Route',
            description: 'Toggle this switch to activate or inactivate your route. In addition to saving the route, active routes will appear in the calendar.',
            target: () => document.getElementById('activate-route-switch') as HTMLElement || null,
          }
        ].filter(step => step.target() !== null)}
        type="default"
        disabledInteraction={true}
      />
    </>
}

export default SaveRouteModal
